import useCallReasons from '@api/hooks/useCallReasons';
import { useContactSupportButton } from '@lib/hooks/useContactSupportButton';
import useCountdownTimer from '@lib/hooks/useCountDownTimer';
import { getCallType } from '@lib/utils/getCallType';
import { inboxLocators } from '@locators/inbox';
import { setCallToCompletedByOthers } from '@modules/InboxModule/callHandlers/callStateHandler';
import InteractionsModule from '@modules/InteractionsModule';
import ModalsModule from '@modules/ModalsModule';
import TasksModule from '@modules/TasksModule';
import ToastModule from '@modules/ToastModule';
import {
  Banner,
  Button,
  ButtonGroup,
  Form,
  Headline,
  Modal,
  Spinner,
  Stack,
  Tag,
  TextAreaOnChangeData,
} from '@servicetitan/design-system';
import { ModalSizes } from '@servicetitan/design-system/dist/components/Modal/components';
import { tokens } from '@servicetitan/tokens/core';
import React, { useMemo, useState } from 'react';
import sharedStrings from 'src/constants/strings';
import strings from './strings';
import useTasks from '@modules/TasksModule/hooks/useTasks';
import { hasGraphQLErrorCode } from '@api/lib/apolloError';

const NOTES_MAX_LENGTH = 5000;
const NOTES_WARNING_LENGTH = 4500;

export type ClassificationModalProps = {
  taskId: string;
  open: boolean;
};

type WrapUpOption = {
  text: string;
  value: string;
};

export const ClassificationModal = ({
  taskId,
  open,
}: ClassificationModalProps) => {
  const [selectedOption, setSelectedOption] = useState<
    WrapUpOption | undefined | null
  >(undefined);
  const [notes, setNotes] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [writeClassificationLoading, setWriteClassificationLoading] =
    useState(false);
  const { taskById } = useTasks();
  const task = taskById[taskId];

  const { count: secondsToComplete } = useCountdownTimer(
    task?.recoveryEndsAt ?? 0,
  );
  const contactSupportProps = useContactSupportButton();
  const closeModal = ModalsModule.closeActiveModal;

  const {
    callReasons: options,
    loading,
    error,
    refetch,
  } = useCallReasons(task?.tenantId);
  const isLoading = loading || task?.loading;

  // filter active ones and map them to text and value
  const selectOptions = useMemo(() => {
    return options
      .filter((option) => option.active && option.name?.trim().length > 0)
      .map((option) => {
        return { text: option.name, value: option.id };
      })
      .sort((a, b) => a.text.localeCompare(b.text));
  }, [options]);

  // filter if search value is provided
  const filteredSelectOptions = useMemo(() => {
    return selectOptions.filter((option) =>
      option.text.toLowerCase().includes(searchValue.trim().toLowerCase()),
    );
  }, [selectOptions, searchValue]);

  const isCloseDisabled = !selectedOption;

  const isTimerCritical = secondsToComplete <= 10;

  const handleWrapUp = async (reasonName?: string, reasonId?: string) => {
    if (!task) {
      console.error(
        `Task with id ${taskId} not found for call classification modal`,
      );
      return;
    }
    setWriteClassificationLoading(true);

    const isLead =
      options.find((option) => option.id === reasonId)?.isLead ?? false;

    const callType = getCallType(isLead, task.attributes?.completedAt);

    try {
      const result = await InteractionsModule.writeClassificationInfo(task, {
        reasonName,
        reasonId,
        initialDispositionNote: notes,
        ...callType,
      });
      if (result) {
        TasksModule.actionTask(task?.id, 'complete-classified');
      }
      closeModal();
    } catch (err: unknown) {
      // ERR_API_INTERACTIONS_UPDATE_ALREADY_COMPLETE is returned when another agent
      // classified the call first. In most cases the real-time task state update
      // handles this automatically, but this catches the edge case where the
      // real-time update hasn't arrived before the agent submits.
      if (
        hasGraphQLErrorCode(err, 'ERR_API_INTERACTIONS_UPDATE_ALREADY_COMPLETE')
      ) {
        setCallToCompletedByOthers(task?.id);
      } else {
        ToastModule.showToast({
          title: strings.writeClassificationToasts.failedUpsert,
          status: 'critical',
          duration: 5000,
          primaryActionName: strings.writeClassificationToasts.failedAction,
          onPrimaryActionClick: () => handleWrapUp(reasonName, reasonId),
          toastBody: sharedStrings.labels.toasts.errorBody,
          ...contactSupportProps,
        });
      }
    } finally {
      setWriteClassificationLoading(false);
    }
  };

  if (!task) {
    console.error(
      `Task with id ${taskId} not found for call classification modal`,
    );
    return null;
  }
  return (
    <Modal.Window
      open={open}
      size={ModalSizes.S}
      id={inboxLocators.callClassificationModal.modalContent}
    >
      {/* Header */}
      <Stack
        className='Modal__header p-x-4 p-y-3'
        alignItems='center'
        justifyContent='space-between'
      >
        <Headline className='m-b-0'>{strings.labels.title}</Headline>
        <Tag
          className={isTimerCritical ? undefined : 'c-blue-500'}
          icon='timer'
          color={isTimerCritical ? 'critical' : tokens.colorBlue100}
          subtle={isTimerCritical}
        >
          {secondsToComplete}s
        </Tag>
      </Stack>
      <Modal.Content scrollable={false}>
        {/* Body */}
        {isLoading && (
          <Stack justifyContent='center' alignItems='center'>
            <Spinner size='small' />
          </Stack>
        )}
        {error && (
          <Banner
            status={'critical'}
            icon
            title={strings.errorBanner.title}
            onPrimaryActionClick={() => refetch()}
            primaryActionName={strings.errorBanner.primaryActionName}
          />
        )}
        {!loading && !error && (
          <Stack direction='column' spacing={2}>
            <Form.AnvilSelect
              scrollHeight={236}
              autoFlipVertically={false}
              portal={false}
              search={{
                placeholder: strings.placeholders.search,
                value: searchValue,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchValue(e.target.value);
                },
              }}
              trigger={{ placeholder: strings.placeholders.select }}
              label={strings.labels.selectLabel}
              options={filteredSelectOptions}
              labelProps={{
                required: true,
              }}
              value={selectedOption}
              onChange={(selected) => {
                setSelectedOption(selected as WrapUpOption);
              }}
              disabled={writeClassificationLoading || isLoading}
              id={inboxLocators.callClassificationModal.reasonFormSelect}
              className='m-b-0'
            />
            <Form.TextArea
              id={inboxLocators.callClassificationModal.notesInput}
              label={strings.labels.notesLabel}
              placeholder={strings.placeholders.notes}
              maxLength={NOTES_MAX_LENGTH}
              // only show the counter when the character limit is close to being reached
              // as the limit is so high and should not be a concern for most of the users
              showCounter={notes.length >= NOTES_WARNING_LENGTH}
              value={notes}
              onChange={(
                _: React.ChangeEvent<HTMLTextAreaElement>,
                data: TextAreaOnChangeData,
              ) => {
                setNotes(data.value);
              }}
              disabled={writeClassificationLoading || isLoading}
            />
          </Stack>
        )}
      </Modal.Content>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            onClick={closeModal}
            id={inboxLocators.callClassificationModal.goBackButton}
          >
            {strings.labels.secondaryAction}
          </Button>
          <Button
            primary
            id={inboxLocators.callClassificationModal.closeAndClassifyButton}
            onClick={() => {
              handleWrapUp(selectedOption?.text, selectedOption?.value);
            }}
            loading={writeClassificationLoading}
            disabled={
              writeClassificationLoading || isCloseDisabled || isLoading
            }
          >
            {strings.labels.primaryAction}
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal.Window>
  );
};
